import { Box, Button } from "@mui/material";
import { useEffect, useState } from "react";
import { type MyApplication, getMyApplications, signOut } from "../../data/api";
import { FlexcellSpinner } from "../../ui/theme/FlexcellMark";
import { useTokens } from "../../ui/theme/theme";
import { LandingLayout } from "./LandingLayout";

// ---
// Somebody who is not platform staff, signed in on the console
//
// Usually a customer's user who signed in to their own app and then pressed Back, since the
// session is the authorization server's and every app on it shares it. The console is no place
// for them, and its menus only confuse, so they are signed out and sent to their app.
//
// Signing out ends the shared session, so the app asks them to sign in again. Chosen over leaving
// them signed in to an administration site they cannot use.
// ---

type Outcome = { kind: "pending" } | { kind: "choose"; applications: MyApplication[] };

export function SendToApplication(): React.JSX.Element {
  const tokens = useTokens();
  const [outcome, setOutcome] = useState<Outcome>({ kind: "pending" });

  useEffect(() => {
    void (async () => {
      // Asked before signing out: the answer needs the session.
      const applications = await getMyApplications().catch((): MyApplication[] => []);
      await signOut().catch(() => undefined);
      const [only] = applications;
      if (applications.length === 1 && only !== undefined) {
        // Replace, so Back does not return here.
        window.location.replace(only.url);
        return;
      }
      setOutcome({ kind: "choose", applications });
    })();
  }, []);

  if (outcome.kind === "pending") {
    return (
      <Box sx={{ minHeight: "100vh", display: "grid", placeItems: "center" }}>
        <FlexcellSpinner size={36} color={tokens.accent.base} title="Redirecting" />
      </Box>
    );
  }

  if (outcome.applications.length === 0) {
    return (
      <LandingLayout
        title="Nothing to open here"
        subtitle="This page is for system administrators. Open the app you normally use, or ask whoever invited you for its address."
      >
        <Button
          variant="outlined"
          size="large"
          fullWidth
          onClick={() => window.location.replace("/")}
        >
          Sign in with another account
        </Button>
      </LandingLayout>
    );
  }

  return (
    <LandingLayout
      title="Where would you like to go?"
      subtitle="This page is for system administrators. Choose the app you want to open."
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {outcome.applications.map((application) => (
          <Button
            key={`${application.tenantId}/${application.clientId}`}
            variant="contained"
            size="large"
            fullWidth
            onClick={() => window.location.replace(application.url)}
          >
            {application.name}
          </Button>
        ))}
      </Box>
    </LandingLayout>
  );
}
